import "./MatchGabor.css";
import { useState, useEffect, useRef, useCallback } from "react";
import BlueCandy from "../../../Assets/Game/MatchGabor/BlueCandy.png";
import YellowCandy from "../../../Assets/Game/MatchGabor/YellowCandy.png";
import OrangeCandy from "../../../Assets/Game/MatchGabor/OrangeCandy.svg";
import PurpleCandy from "../../../Assets/Game/MatchGabor/PurpleCandy.png";
import GreenCandy from "../../../Assets/Game/MatchGabor/GreenCandy.png";
import RedCandy from "../../../Assets/Game/MatchGabor/RedCandy.png";
import blank from "../../../Assets/Game/candy/black.png";
import { useNavigate } from "react-router";
import AuthUser from "../../../Hooks/authUser";
import moment from "moment";
import { server_url } from "../../../Config/API";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import GameStatsIndicator from "../GameStatsIndicator";

const grid = 8;

const colors = [
  RedCandy,
  BlueCandy,
  GreenCandy,
  YellowCandy,
  OrangeCandy,
  PurpleCandy,
];

const MatchGabor = () => {
  const [currentColorArrangement, setCurrentColorArrangement] = useState([]);
  const navigate = useNavigate();
  const [squareBeingDragged, setSquareBeingDragged] = useState(null);
  const [squareBeingReplaced, setSquareBeingReplaced] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(600);
  const [isGameOver, setIsGameOver] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const [matchGaborData, setMatchGaborData] = useState([]);
  const { userInfo } = AuthUser();
  const date = useRef(moment().format("YYYY-MM-D")).current;
  const time = useRef(moment().format("h:mm a")).current;

  const previousStateRef = useRef(null);
  const scoreRef = useRef(score);
  const timeRef = useRef(timeLeft);
  const boardRef = useRef([]);

  scoreRef.current = score;
  timeRef.current = timeLeft;
  boardRef.current = currentColorArrangement;

  const fetchFunction = async () => {
    try {
      const response = await fetch(
        `${server_url}/matchGabor/specific?fieldName=userId&fieldValue=${
          userInfo?._id
        }&fieldName2=date&fieldValue2=${date}`
      );
      const data = await response.json();
      setMatchGaborData(data?.data || []);
      if (data?.data && data?.data.length) {
        setScore(data?.data[0]?.score);
        previousStateRef.current = data?.data;
      }
    } catch (error) {
      console.error("Error fetching match gabor data:", error);
    }
  };

  useEffect(() => {
    if (matchGaborData?.length) {
      setTimeLeft(matchGaborData[0]?.remainingTime);
      setScore(matchGaborData[0]?.score);
    }
  }, [matchGaborData]);

  useEffect(() => {
    fetchFunction();
  }, []);

  const insertFunction = useCallback(() => {
    if (previousStateRef?.current?.length) {
      UpdateHooks(
        `${server_url}/matchGabor/updateMatchGabor/${previousStateRef?.current[0]?._id}`,
        {
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        }
      );
    } else {
      PostHooks(
        `${server_url}/matchGabor/addMatchGabor`,
        {
          gameName: "Match Gabor Monocular",
          userId: userInfo?._id,
          userName: userInfo?.name,
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        },
        "Match Gabor score updated !"
      );
    }
  }, [userInfo, date, time]);

  const insertFunctionRef = useRef(insertFunction);
  useEffect(() => {
    insertFunctionRef.current = insertFunction;
  }, [insertFunction]);

  const createBoard = () => {
    const randomColorArrangement = [];
    for (let i = 0; i < 64; i++) {
      let randomColor = colors[Math.floor(Math.random() * colors.length)];
      randomColorArrangement.push(randomColor);
    }
    setCurrentColorArrangement(randomColorArrangement);
  };

  const toggleFullScreenRef = useRef(null);
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((error) => {
          console.error("Error entering full screen:", error);
        });
      }
    }
    setIsFullScreen(!isFullScreen);
  };
  toggleFullScreenRef.current = toggleFullScreen;

  useEffect(() => {
    toggleFullScreen();
    createBoard();
  }, []);

  const checkForColumnOfFour = (board) => {
    let matchFound = false;
    for (let i = 0; i <= 39; i++) {
      const columnOfFour = [i, i + grid, i + grid * 2, i + grid * 3];
      const decidingColor = board[i];
      if (decidingColor === blank) continue;

      if (columnOfFour.every((square) => board[square] === decidingColor)) {
        columnOfFour.forEach((square) => {
          board[square] = blank;
        });
        setScore((prev) => prev + 4);
        matchFound = true;
      }
    }
    return matchFound;
  };

  const checkForColumnOfThree = (board) => {
    let matchFound = false;
    for (let i = 0; i <= 47; i++) {
      const columnOfThree = [i, i + grid, i + grid * 2];
      const decidedColor = board[i];
      if (decidedColor === blank) continue;

      if (columnOfThree.every((square) => board[square] === decidedColor)) {
        columnOfThree.forEach((square) => {
          board[square] = blank;
        });
        setScore((prev) => prev + 3);
        matchFound = true;
      }
    }
    return matchFound;
  };

  const checkForRowOfThree = (board) => {
    let matchFound = false;
    const notValid = [6, 7, 14, 15, 22, 23, 30, 31, 38, 39, 46, 47, 54, 55, 63, 64];
    for (let i = 0; i < 64; i++) {
      if (notValid.includes(i)) continue;
      const rowOfThree = [i, i + 1, i + 2];
      const decidingColor = board[i];
      if (decidingColor === blank) continue;

      if (rowOfThree.every((square) => board[square] === decidingColor)) {
        rowOfThree.forEach((square) => {
          board[square] = blank;
        });
        setScore((prev) => prev + 3);
        matchFound = true;
      }
    }
    return matchFound;
  };

  const checkForRowOfFour = (board) => {
    let matchFound = false;
    const notValid = [
      5, 6, 7, 13, 14, 15, 21, 22, 23, 29, 30, 31, 37, 38, 39, 45, 46, 47, 53,
      54, 55, 62, 63, 64,
    ];
    for (let i = 0; i < 64; i++) {
      if (notValid.includes(i)) continue;
      const rowOfFour = [i, i + 1, i + 2, i + 3];
      const decidingColor = board[i];
      if (decidingColor === blank) continue;

      if (rowOfFour.every((square) => board[square] === decidingColor)) {
        rowOfFour.forEach((square) => {
          board[square] = blank;
        });
        setScore((prev) => prev + 4);
        matchFound = true;
      }
    }
    return matchFound;
  };

  const moveIntoSquareBelow = (board) => {
    let moved = false;
    for (let i = 0; i < 64 - grid; i++) {
      const firstRow = [0, 1, 2, 3, 4, 5, 6, 7];
      if (firstRow.includes(i) && board[i] === blank) {
        board[i] = colors[Math.floor(Math.random() * colors.length)];
        moved = true;
      }
      if (board[i + grid] === blank) {
        board[i + grid] = board[i];
        board[i] = blank;
        moved = true;
      }
    }
    return moved;
  };

  const dragStart = (e) => {
    setSquareBeingDragged(e.target);
  };

  const dragDrop = (e) => {
    e.preventDefault();
    setSquareBeingReplaced(e.target);
  };

  const dragEnd = () => {
    if (!squareBeingDragged || !squareBeingReplaced) return;

    const squareBeingDraggedId = parseInt(squareBeingDragged.getAttribute("data-id"));
    const squareBeingReplacedId = parseInt(squareBeingReplaced.getAttribute("data-id"));

    const newArrangement = [...currentColorArrangement];
    newArrangement[squareBeingReplacedId] = squareBeingDragged.getAttribute("src");
    newArrangement[squareBeingDraggedId] = squareBeingReplaced.getAttribute("src");

    const validMoves = [
      squareBeingDraggedId - 1,
      squareBeingDraggedId - grid,
      squareBeingDraggedId + 1,
      squareBeingDraggedId + grid,
    ];

    const validMove = validMoves.includes(squareBeingReplacedId);
    if (validMove) {
      const isAColumnOfFour = checkForColumnOfFour(newArrangement);
      const isARowOfFour = checkForRowOfFour(newArrangement);
      const isAColumnOfThree = checkForColumnOfThree(newArrangement);
      const isARowOfThree = checkForRowOfThree(newArrangement);

      if (isARowOfThree || isARowOfFour || isAColumnOfFour || isAColumnOfThree) {
        setCurrentColorArrangement(newArrangement);
        setSquareBeingDragged(null);
        setSquareBeingReplaced(null);
      }
    }
  };

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          setIsGameOver(true);
          insertFunctionRef.current();
          navigate(-1);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (isGameOver || boardRef.current.length === 0) return;

      const workingBoard = [...boardRef.current];
      const match4Col = checkForColumnOfFour(workingBoard);
      const match3Col = checkForColumnOfThree(workingBoard);
      const match4Row = checkForRowOfFour(workingBoard);
      const match3Row = checkForRowOfThree(workingBoard);
      const moved = moveIntoSquareBelow(workingBoard);

      if (match4Col || match3Col || match4Row || match3Row || moved) {
        setCurrentColorArrangement(workingBoard);
      }
    }, 100);

    return () => clearInterval(timer);
  }, [isGameOver]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.keyCode === 27) {
        insertFunctionRef.current();
        window.history.back();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, []);

  const min = Math.floor(timeLeft / 60);
  return (
    <div className="min-h-screen inset-0 fixed bg-white flex items-center justify-center">
      <GameStatsIndicator score={score} timeLeft={timeLeft} />
      <div className="w-full bg-[#7F7F7F] grid grid-cols-8 gap-0">
        {currentColorArrangement.map((currentColor, index) => (
          <img
            alt="icon"
            className="rotate rounded-full opacity-full"
            src={currentColor}
            key={index}
            draggable={true}
            data-id={index}
            onDragStart={dragStart}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => e.preventDefault()}
            onDragLeave={(e) => e.preventDefault()}
            onDrop={dragDrop}
            onDragEnd={dragEnd}
          />
        ))}
      </div>
    </div>
  );
};

export default MatchGabor;
